import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import ErrorBoundary from './ErrorBoundary';
import reportWebVitals from './reportWebVitals';
import { ThemeProvider, initializeTheme } from './themes/ThemeContext';

// BUILD 260: set the html data-theme attribute before React mounts so a
// non-default persisted theme doesn't flash through the default theme on
// first paint.
initializeTheme();

// Console logger (dev only). Was a hardcoded <script> in index.html, which
// shipped it to production where it just self-disabled after a wasted fetch
// and a console line. It exists to feed the localhost:3002 log server during
// local development, so only inject it there.
if (process.env.NODE_ENV === 'development') {
    const consoleLoggerScript = document.createElement('script');
    consoleLoggerScript.src = `${process.env.PUBLIC_URL || ''}/console-logger.js`;
    document.head.appendChild(consoleLoggerScript);
}

// BUILD 186: NUCLEAR OPTION - Suppress CRA error overlay for Firebase permission errors
// CRA's error overlay uses a special iframe injected by webpack-dev-server
// We need to intercept it at multiple levels
if (process.env.NODE_ENV === 'development') {
  // Method 1: Intercept the error overlay iframe creation
  const originalCreateElement = document.createElement.bind(document);
  document.createElement = function(tagName: string, options?: ElementCreationOptions) {
    const element = originalCreateElement(tagName, options);
    if (tagName.toLowerCase() === 'iframe') {
      // Monitor iframe for error overlay
      setTimeout(() => {
        try {
          const iframeDoc = (element as HTMLIFrameElement).contentDocument;
          if (iframeDoc?.body?.textContent?.includes('Missing or insufficient permissions')) {
            element.style.display = 'none';
            element.remove();
            console.warn('🔐 BUILD 186: Removed Firebase permission error overlay');
          }
        } catch (e) {
          // Cross-origin iframe, ignore
        }
      }, 100);
    }
    return element;
  };

  // Method 2: MutationObserver to catch and remove error overlay
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLIFrameElement) {
          setTimeout(() => {
            try {
              const doc = node.contentDocument || node.contentWindow?.document;
              if (doc?.body?.textContent?.includes('Missing or insufficient permissions')) {
                node.style.display = 'none';
                node.remove();
                console.warn('🔐 BUILD 186: Removed Firebase permission error overlay (MutationObserver)');
              }
            } catch (e) {
              // Cross-origin, check by ID
              if (node.id?.includes('webpack-dev-server') || node.id?.includes('react-error-overlay')) {
                // Check after a delay when content is loaded
                setTimeout(() => {
                  try {
                    const doc = node.contentDocument || node.contentWindow?.document;
                    if (doc?.body?.textContent?.includes('Missing or insufficient permissions')) {
                      node.style.display = 'none';
                      node.remove();
                    }
                  } catch (e2) { /* ignore */ }
                }, 500);
              }
            }
          }, 50);
        }
        // Also check for div-based overlays
        if (node instanceof HTMLDivElement) {
          if (node.textContent?.includes('Missing or insufficient permissions')) {
            node.style.display = 'none';
            node.remove();
            console.warn('🔐 BUILD 186: Removed Firebase permission error div overlay');
          }
        }
      });
    });
  });

  // Start observing once DOM is ready
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }

  // Method 3: Periodically check and remove any error overlays
  setInterval(() => {
    // Check for CRA error overlay iframe
    document.querySelectorAll('iframe').forEach((iframe) => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc?.body?.textContent?.includes('Missing or insufficient permissions')) {
          iframe.style.display = 'none';
          iframe.remove();
          console.warn('🔐 BUILD 186: Removed Firebase permission error overlay (interval check)');
        }
      } catch (e) { /* cross-origin, ignore */ }
    });

    // Check for any visible error text and hide parent containers
    document.querySelectorAll('*').forEach((el) => {
      if (el instanceof HTMLElement &&
          el.style.position === 'fixed' &&
          el.textContent?.includes('Missing or insufficient permissions')) {
        el.style.display = 'none';
        console.warn('🔐 BUILD 186: Hidden Firebase permission error container');
      }
    });
  }, 200);
}

// BUILD 186: Install Firebase permission error handler BEFORE React loads
// This catches unhandled promise rejections early, preventing CRA error overlay
const isFirebasePermissionError = (error: unknown): boolean => {
  if (!error) return false;
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorCode = (error as any)?.code;
  return (
    errorMessage.includes('Missing or insufficient permissions') ||
    errorMessage.includes('permission-denied') ||
    errorCode === 'permission-denied'
  );
};

// Flag to track if we've already handled a permission error
let firebaseAuthErrorHandled = false;

// BUILD 214: Track sign-in cooldown (mirrors firebaseAuthErrorHandler.ts)
let signInStartedAt: number | null = null;
const SIGN_IN_COOLDOWN_MS = 10000;

// BUILD 214: Expose functions globally so App.tsx can call them
(window as any).__markSignInStarted = () => {
  signInStartedAt = Date.now();
  console.log('🔐 BUILD 214 index.tsx: Sign-in started - suppressing permission errors');
};
(window as any).__markSignInComplete = () => {
  signInStartedAt = null;
  console.log('🔐 BUILD 214 index.tsx: Sign-in complete');
};

const isInSignInCooldown = (): boolean => {
  if (!signInStartedAt) return false;
  const elapsed = Date.now() - signInStartedAt;
  if (elapsed < SIGN_IN_COOLDOWN_MS) return true;
  signInStartedAt = null;
  return false;
};

// Dispatch event and show UI notification
const handleFirebaseAuthError = (error: unknown) => {
  console.log('🔐🔐🔐 BUILD 214 index.tsx: handleFirebaseAuthError() CALLED');
  console.log('🔐 Error:', error instanceof Error ? error.message : String(error));
  console.log('🔐 Stack trace:', new Error().stack);

  if (firebaseAuthErrorHandled) {
    console.log('🔐 BUILD 214: Already handled, skipping');
    return;
  }

  // BUILD 214: Check cooldown
  if (isInSignInCooldown()) {
    console.log('🔐 BUILD 214 index.tsx: Suppressed - sign-in in progress');
    return;
  }

  firebaseAuthErrorHandled = true;

  console.warn('🔐 BUILD 214 index.tsx: DISPATCHING firebase-auth-error (will show popup!)');

  // Dispatch event for App.tsx to handle
  window.dispatchEvent(new CustomEvent('firebase-auth-error', {
    detail: { error, requiresSignIn: true }
  }));

  // Reset flag after a delay to allow future errors
  setTimeout(() => { firebaseAuthErrorHandled = false; }, 5000);
};

// CRITICAL: Patch the error overlay by overriding window.onerror and onunhandledrejection
// This runs BEFORE React's error overlay attaches its listeners
const originalOnError = window.onerror;
window.onerror = function(message, source, lineno, colno, error) {
  if (isFirebasePermissionError(error) || (typeof message === 'string' && message.includes('Missing or insufficient permissions'))) {
    handleFirebaseAuthError(error || message);
    return true; // Prevent default handling
  }
  return originalOnError ? originalOnError.call(this, message, source, lineno, colno, error) : false;
};

const originalOnUnhandledRejection = window.onunhandledrejection;
(window as any).onunhandledrejection = function(event: PromiseRejectionEvent) {
  if (isFirebasePermissionError(event.reason)) {
    handleFirebaseAuthError(event.reason);
    event.preventDefault();
    return;
  }
  if (originalOnUnhandledRejection) {
    (originalOnUnhandledRejection as any).call(window, event);
  }
};

// Also use addEventListener for additional coverage
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  if (isFirebasePermissionError(event.reason)) {
    handleFirebaseAuthError(event.reason);
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, true);

window.addEventListener('error', (event: ErrorEvent) => {
  if (isFirebasePermissionError(event.error)) {
    handleFirebaseAuthError(event.error);
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}, true);

// Also intercept console.error as CRA uses this for error detection
const originalConsoleError = console.error;
console.error = function(...args: any[]) {
  const errorString = args.map(a => String(a)).join(' ');
  if (errorString.includes('Missing or insufficient permissions') || errorString.includes('permission-denied')) {
    // Suppress this error from console to prevent CRA error overlay
    console.warn('🔐 BUILD 186: Suppressed Firebase permission error from console.error');
    handleFirebaseAuthError(args[0]);
    return;
  }
  originalConsoleError.apply(console, args);
};

console.log('🔐 BUILD 186: Firebase permission error handlers installed (including console.error)');

// BUILD 188: ServiceWorker recovery function for broken states
async function recoverServiceWorker(): Promise<boolean> {
  console.log('🔧 BUILD 188: Attempting ServiceWorker recovery...');
  try {
    // Unregister all existing service workers
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.unregister();
      console.log('🗑️ BUILD 188: Unregistered ServiceWorker:', registration.scope);
    }

    // Clear all caches
    const cacheKeys = await caches.keys();
    for (const key of cacheKeys) {
      await caches.delete(key);
      console.log('🗑️ BUILD 188: Deleted cache:', key);
    }

    console.log('✅ BUILD 188: ServiceWorker recovery complete - reloading...');
    return true;
  } catch (e) {
    console.error('❌ BUILD 188: Recovery failed:', e);
    return false;
  }
}

// BUILD 188: Versioned Service Worker with automatic cache invalidation and recovery
// Only register in production — in dev, webpack-dev-server handles everything
// and SW registration fights with the dev server causing "invalid state" errors
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('✅ BUILD 188: Service Worker registered:', registration.scope);

        // Check for updates every 30 seconds
        setInterval(() => {
          registration.update();
        }, 30000);

        // Listen for new service worker waiting
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New version available - notify and reload
                console.log('🔄 BUILD 188: New version available - reloading...');
                newWorker.postMessage('SKIP_WAITING');
                window.location.reload();
              }
            });
          }
        });
      })
      .catch(async (error) => {
        const errorMessage = String(error?.message || error);
        console.error('❌ BUILD 188: Service Worker registration failed:', error);

        // BUILD 188: Auto-recover from "shutdown" or other critical errors
        if (errorMessage.includes('shutdown') || errorMessage.includes('security') || errorMessage.includes('script')) {
          console.warn('🔧 BUILD 188: Detected broken ServiceWorker state - initiating recovery...');

          // Check if we haven't already tried recovery this session
          const recoveryAttempted = sessionStorage.getItem('sw_recovery_attempted');
          if (!recoveryAttempted) {
            sessionStorage.setItem('sw_recovery_attempted', 'true');
            const recovered = await recoverServiceWorker();
            if (recovered) {
              // Brief delay then reload
              setTimeout(() => window.location.reload(), 500);
            }
          } else {
            console.warn('⚠️ BUILD 188: Recovery already attempted this session - manual intervention may be needed');
          }
        }
      });
  });
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
document.getElementById('static-legal-links')?.remove();

const stripKnownBasePath = (pathname: string): string => {
  const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  let normalized = pathname || '/';

  if (publicUrl && publicUrl !== '/' && normalized.startsWith(publicUrl)) {
    normalized = normalized.slice(publicUrl.length) || '/';
  }

  // GitHub Pages serves the mirror under /redo-web-app even though production
  // Firebase hosting is rooted at /. Normalize both before route selection.
  if (normalized.startsWith('/redo-web-app')) {
    normalized = normalized.slice('/redo-web-app'.length) || '/';
  }

  return normalized.startsWith('/') ? normalized : `/${normalized}`;
};

const appHashViews = new Set([
  'archive',
  'graveyard',
  'node-debugger',
  'validation-report',
  'infirmary',
  'correction-panel',
  'google-tasks',
  'google-calendar',
  'task-sync',
  'privacy',
  'terms',
  'cookies',
  'about',
  'create-task',
  'history',
  'task-details',
  'auth-management',
  'data-management',
  'profile-settings',
  'calendar',
  'firebase-test',
  'task-filter',
  'ranking-calculator',
  'ranking-playground',
  'firebase-admin',
  'dag-visualizer',
  'dag-debugger',
  'storage-inspector',
  'console-logger',
  'history-viewer'
]);

const appPath = stripKnownBasePath(window.location.pathname);
const hashView = window.location.hash.replace(/^#/, '').split('/')[0];
const shouldRenderHomepageAltOne = appPath.startsWith('/homepage/alt_1');
// BUILD 299: /auth/callback is the Knomee SIWE redirect target. Without this
// the top-level gate would route the callback URL to HomepageApp (marketing
// landing) and the SPA's KnomeeCallbackView — which lives behind App.tsx's
// `currentView === 'knomee-callback'` branch — would never get a chance to
// parse the URL fragment and finish the sign-in. Symptom before fix: user
// completes Knomee approval, lands on redotasks.com/auth/callback#address=…&signature=…,
// sees the marketing landing instead of the signing flow, signature is
// dropped on the floor.
const shouldRenderApp =
  appPath === '/app' ||
  appPath.startsWith('/app/') ||
  ['/privacy', '/tos', '/terms', '/cookies', '/about', '/auth/callback'].includes(appPath) ||
  appHashViews.has(hashView);

const render = async () => {
  if (shouldRenderHomepageAltOne) {
    const { default: HomepageAltOne } = await import('./homepage/HomepageAltOne');
    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <HomepageAltOne />
        </ErrorBoundary>
      </React.StrictMode>
    );
  } else if (!shouldRenderApp) {
    const { default: HomepageApp } = await import('./homepage/HomepageApp');
    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <HomepageApp />
        </ErrorBoundary>
      </React.StrictMode>
    );
  } else {
    const { default: App } = await import('./App');
    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <ThemeProvider>
            <App />
          </ThemeProvider>
        </ErrorBoundary>
      </React.StrictMode>
    );
  }
};

void render();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
